import axios from "axios";

import { postRefresh } from "@/api/auth/postRefresh";
import { ROUTES } from "@/config/routes/paths";
import { ENV } from "@/utils/env";
import { decodeJwtExpWithBuffer } from "@/utils/token";

const BASE_URL = ENV.serviceUrl;
const NO_AUTH_ROUTES = ["/admins/api/v1/users/login/", "/users/logout/", "/users/refresh/"];

export const getAccessToken = () => {
  if (typeof window !== "undefined") {
    return localStorage.getItem("access_token");
  }
  return null;
};

const clearTokensAndRedirect = () => {
  if (typeof window !== "undefined") {
    localStorage.removeItem("access_token");
    localStorage.removeItem("userid");
    localStorage.removeItem("username");
    window.location.href = ROUTES.LOGIN;
  }
};

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    "Accept-Language": "ko-KR",
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const waitForTokenRefresh = () => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  });
};

// 요청 인터셉터 - headers
axiosInstance.interceptors.request.use(
  async (config) => {
    const url = config.url || "";

    if (NO_AUTH_ROUTES.includes(url)) {
      delete config.headers.Authorization;
      return config;
    }

    const token = getAccessToken();

    if (token) {
      // 토큰 만료 1분 전 체크
      const isExpiredOrExpiring = !decodeJwtExpWithBuffer(token, 1 * 60);

      if (isExpiredOrExpiring) {
        // 이미 갱신 중인 경우 대기
        if (isRefreshing) {
          try {
            const newToken = await waitForTokenRefresh();
            config.headers.Authorization = `Bearer ${newToken}`;
            return config;
          } catch (error) {
            return Promise.reject(error);
          }
        }

        isRefreshing = true;

        try {
          const response = await postRefresh();
          const newAccessToken = response.data.data.token;
          localStorage.setItem("access_token", newAccessToken);
          config.headers.Authorization = `Bearer ${newAccessToken}`;

          processQueue(null, newAccessToken);
          return config;
        } catch (error) {
          processQueue(error, null);
          clearTokensAndRedirect();

          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      } else {
        // 토큰이 유효한 경우
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 응답 인터셉터 - refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const response = await postRefresh();
        const accessToken = response.data.data.token;

        if (typeof window !== "undefined") {
          localStorage.setItem("access_token", accessToken);
        }

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        clearTokensAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

export const refreshTokenAndSaveToLocalStorage = async (): Promise<string> => {
  try {
    const response = await postRefresh();
    const newToken = response.data.data.token;
    localStorage.setItem("access_token", newToken);
    return newToken;
  } catch (error) {
    clearTokensAndRedirect();
    throw error;
  }
};
